import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { canPurchase } from "@/lib/products";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const add = (product) => {
    if (!canPurchase(product)) { toast.info("Please enquire for a confirmed price before adding this item."); return; }
    const existingItem = items.find((item) => item.product.id === product.id);
    if (existingItem?.quantity >= 100) { toast.info("Maximum 100 packs of one item per order. Contact us for bulk quantities."); return; }
    if (!existingItem && items.length >= 60) { toast.info("Maximum 60 different items per order. Contact us for a bulk order."); return; }
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, quantity: Math.min(100, i.quantity + 1) } : i
        );
      }
      return prev.length >= 60 ? prev : [...prev, { product, quantity: 1 }];
    });
    toast.success(`${product.name}${product.pack_group_id ? ` (${product.unit})` : ""} added to your basket`);
  };

  const remove = (id) =>
    setItems((prev) => prev.filter((i) => i.product.id !== id));

  const setQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.product.id !== id)
        : prev.map((i) => (i.product.id === id && Number.isInteger(qty) ? { ...i, quantity: Math.min(100, qty) } : i))
    );

  const clear = () => setItems([]);

  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce((sum, i) => sum + i.quantity * i.product.price, 0);

  const value = { items, add, remove, setQty, clear, count, total, open, setOpen };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
